import {Helmet} from '@dr.pogodin/react-helmet';
import {showNotification} from 'platform/components';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {
  HeaderActionType,
  useGetIssueNoteQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DetailTemplate,
  DetailTemplateHeader,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  printPDF,
  useFileNotification,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, generateHashFromObjects, useNavigate} from 'shared/web';

import {CostCorrections} from './(sections)/CostCorrections/CostCorrections';
import {Documents} from './(sections)/Documents/Documents';
import {IssueNoteCorrections} from './(sections)/IssueNoteCorrections/IssueNoteCorrections';
import {Overview} from './(sections)/Overview/Overview';

export function IssueNoteDetail() {
  const navigate = useNavigate();
  const {issueNoteId} = useWarehouseParams();

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const {
    data: issueNote,
    isLoading: isIssueNoteLoading,
    isFetching: isIssueNoteFetching,
    isError: hasIssueNoteError,
  } = useGetIssueNoteQuery({issueNoteId});

  const {
    data: issueNoteAction,
    isFetching: isIssueNoteActionFetching,
    isError: hasIssueNoteActionError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: issueNoteId, resource: 'issue-note'},
    {skip: isNilOrEmpty(issueNoteId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-issue-note', contextId: issueNoteId},
    {skip: !issueNoteId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-issue-note',
    contextId: issueNoteId,
  });

  const [putIssueNoteAction, {isLoading: isIssueNoteActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const handleActionClick = (actionKey: string, type?: HeaderActionType) => {
    const navigatePaths: Record<string, string | undefined> = {
      complete: warehouseRoutes.issueNotes,
      delete: warehouseRoutes.issueNotes,
    };

    const handleSuccess = () => {
      const path = navigatePaths[actionKey];
      if (path) {
        navigate(path);
      }
    };

    if (type === 'PRINTOUT' || actionKey === 'prntIssueNote') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: issueNoteId,
          originEntityPrintoutType: 'prntIssueNote',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
              onError: () => showNotification.error(),
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.issueNoteDetailDocuments, {
              params: {id: issueNoteId},
            })
          );
        })
        .catch(handleApiError);
    }

    putIssueNoteAction({
      id: issueNoteId,
      resource: 'issue-note',
      body: {actionKey},
    })
      .unwrap()
      .then(() => handleSuccess())
      .then(refreshDocumentsCount)
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: issueNoteAction?.actions,
    callback: handleActionClick,
    isLoading: isIssueNoteActionLoading || isPostPrintoutOnDemandLoading,
  });

  const isLoading = isIssueNoteLoading || isIssueNoteActionFetching;

  const isError = hasIssueNoteError || hasIssueNoteActionError;

  const issueNoteTitle = `${i18n.t('page.warehouse.labels.issueNote')} ${defaultTo(
    '',
    issueNote?.issueNoteInformation.number
  )}`;

  const header: DetailTemplateHeader = {
    title: issueNoteTitle,
    isEntityLoading: isIssueNoteFetching,
    actions: headerActions,
  };

  const navigation: NavigationItem[] = [
    {
      id: 'overview',
      label: i18n.t('general.labels.overview'),
      href: composePath(warehouseRoutes.issueNoteDetailOverview, {
        params: {id: issueNoteId},
      }),
      content: (
        <Overview
          issueNote={issueNote}
          isIssueNoteLoading={isIssueNoteLoading}
          data-testid={testIds.warehouse.issueNoteDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.issueNoteDetailOverview('navigation.overview'),
    },
    {
      id: 'issue-note-corrections',
      label: i18n.t('general.labels.issueNoteCorrections'),
      href: composePath(warehouseRoutes.issueNoteDetailIssueNoteCorrections, {
        params: {id: issueNoteId},
      }),
      content: <IssueNoteCorrections isIssueNoteLoading={isIssueNoteLoading} />,
      'data-testid': testIds.warehouse.issueNoteDetailIssueNoteCorrections(
        'navigation.issueNoteCorrections'
      ),
    },
    {
      id: 'costCorrections',
      label: i18n.t('general.labels.costCorrections'),
      href: composePath(warehouseRoutes.issueNoteDetailCostCorrections, {
        params: {id: issueNoteId},
      }),
      content: <CostCorrections isIssueNoteLoading={isIssueNoteLoading} />,
      'data-testid': testIds.warehouse.issueNoteDetailCostCorrections('navigation.costCorrections'),
    },
    {
      id: 'documents',
      label: i18n.t('general.labels.documents'),
      href: composePath(warehouseRoutes.issueNoteDetailDocuments, {
        params: {id: issueNoteId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.issueNoteDetailDocuments('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.issueNoteDetailDocuments('navigation.documents'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.issueNoteDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(issueNote)}
        isLoading={isLoading}
        isError={isError}
        isLocked={isIssueNoteActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.issueNoteDetailOverview('page')}
      />
    </>
  );
}
